"use client";

import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/app/v2/_components/button/button";

const ease = [0.16, 1, 0.3, 1] as const;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease, delay },
});

interface BucketerLandingProps {
  onConnect: () => void;
}

export function BucketerLanding({ onConnect }: BucketerLandingProps) {
  return (
    <section className="py-28 md:py-40 relative overflow-hidden">
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        {/* Headline — big, confident, no fluff */}
        <motion.h2
          {...fadeUp(0.1)}
          className="text-[2.75rem] md:text-6xl lg:text-7xl font-extrabold text-foreground leading-[1.05] tracking-tight text-center"
        >
          How much commission is hiding
          <br />
          in your{" "}
          <span className="relative inline-block">
            CRM
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, ease, delay: 0.8 }}
              className="absolute -bottom-1 left-0 right-0 h-[3px] bg-emerald-500 origin-left"
            />
          </span>
          ?
        </motion.h2>

        <motion.p
          {...fadeUp(0.3)}
          className="mt-6 text-lg md:text-xl text-foreground/50 text-center max-w-lg mx-auto leading-relaxed"
        >
          We look up property values for every contact and show you the exact dollar amount. Free. Under 2 minutes.
        </motion.p>

        <motion.div {...fadeUp(0.5)} className="mt-12 flex justify-center">
          <Button
            variant="bold"
            size="2xl"
            onClick={onConnect}
            className="text-lg px-12 h-16 rounded-2xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-shadow"
          >
            Connect my CRM
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>

        <motion.div {...fadeUp(0.65)} className="mt-16 flex flex-col items-center gap-3">
          <div className="flex items-center justify-center gap-2 text-[13px] text-foreground/35">
            <Step n={1} text="Connect" />
            <span aria-hidden>/</span>
            <Step n={2} text="Enrich" />
            <span aria-hidden>/</span>
            <Step n={3} text="Results" />
          </div>

          <p className="text-xs text-foreground/25">Follow Up Boss, Sierra, CINC, and 14 more CRMs</p>
        </motion.div>
      </div>
    </section>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-[18px] h-[18px] rounded-full bg-foreground/6 text-foreground/30 text-[10px] font-bold flex items-center justify-center shrink-0">
        {n}
      </span>
      {text}
    </span>
  );
}
